/**
 * Shared REST client for the Enterprise Support Bank services (task 21.2;
 * Slice C3 routing update).
 *
 * The account, beneficiary, transfer, insights, reversal, and notification
 * views all call their services through this one module so request handling is
 * uniform: every call
 *
 * - is routed to the correct **per-service API Gateway** base URL by path
 *   prefix (see {@link baseUrlForPath}) — each service has its own HTTPS
 *   endpoint, configured via Vite env with the live deployed URL baked in as a
 *   default;
 * - attaches the in-memory Cognito **idToken** as an `Authorization: Bearer`
 *   header (Slice C3 — the API Gateway JWT authorizer validates the idToken's
 *   `aud`; the token is read only from {@link getAccessToken}, never storage);
 * - decodes non-2xx responses through the standard error envelope and rethrows
 *   them as {@link ApiError}, so views branch on a stable `code` and render the
 *   non-sensitive `message` inline.
 *
 * The SPA does **not** send an `x-customer-id` header — services read identity
 * from the authorizer-mapped `x-customer-id` (the verified `sub`).
 *
 * This module *consumes* the auth module (its {@link ApiError}/envelope types
 * and the in-memory token store) but never mutates it.
 */

import {
  ApiError,
  type ApiErrorBody,
  isApiErrorEnvelope,
} from '../auth/errors.js';
import { getAccessToken } from '../auth/tokenStore.js';

/** Trim trailing slashes so path joins are unambiguous. */
function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

/**
 * Per-service API Gateway base URLs, injected at build time by Vite with the
 * live deployed endpoint baked in as a default so the SPA works even when the
 * env vars are not set at build time. Each service is its own HTTPS endpoint.
 */
const BALANCE_API_URL = trimTrailingSlash(
  (import.meta.env.VITE_BALANCE_API_URL as string | undefined) ??
    'https://72b3wvrk0j.execute-api.af-south-1.amazonaws.com',
);
const TRANSFER_API_URL = trimTrailingSlash(
  (import.meta.env.VITE_TRANSFER_API_URL as string | undefined) ??
    'https://k3qz8e6otc.execute-api.af-south-1.amazonaws.com',
);
const INSIGHTS_API_URL = trimTrailingSlash(
  (import.meta.env.VITE_INSIGHTS_API_URL as string | undefined) ??
    'https://ds97t4o7m4.execute-api.af-south-1.amazonaws.com',
);
const REVERSAL_API_URL = trimTrailingSlash(
  (import.meta.env.VITE_REVERSAL_API_URL as string | undefined) ??
    'https://ch7niaxf2h.execute-api.af-south-1.amazonaws.com',
);
const BENEFICIARY_API_URL = trimTrailingSlash(
  (import.meta.env.VITE_BENEFICIARY_API_URL as string | undefined) ??
    'https://qfh0mf9tma.execute-api.af-south-1.amazonaws.com',
);
const NOTIFICATION_API_URL = trimTrailingSlash(
  (import.meta.env.VITE_NOTIFICATION_API_URL as string | undefined) ??
    'https://8zue3qf6x6.execute-api.af-south-1.amazonaws.com',
);

/**
 * Resolve the correct per-service base URL for a request path.
 *
 * Ordering matters: the more specific suffixes are checked BEFORE the generic
 * prefixes so the shared `/accounts` and `/transactions` namespaces route
 * correctly:
 *
 * | Path                                | Service      |
 * | ----------------------------------- | ------------ |
 * | `/accounts/{id}/insights`           | insights     |
 * | `/accounts`, `/accounts/{id}`, `…/transactions` | balance |
 * | `/beneficiaries`                    | beneficiary  |
 * | `/transfers`                        | transfer     |
 * | `/transactions/{id}/reverse`        | reversal     |
 * | `/notifications`                    | notification |
 *
 * Falls back to the balance endpoint for any unmapped path (none exist today).
 */
export function baseUrlForPath(path: string): string {
  // Strip any query string before matching (callers pass bare paths, but be safe).
  const p = path.split('?')[0] ?? path;

  // Insights lives under /accounts/*/insights — check the suffix BEFORE the
  // generic /accounts prefix.
  if (p.startsWith('/accounts')) {
    if (p.endsWith('/insights')) {
      return INSIGHTS_API_URL;
    }
    return BALANCE_API_URL;
  }
  // Reversal lives under /transactions/*/reverse — check before any other
  // /transactions handling.
  if (p.startsWith('/transactions')) {
    if (p.endsWith('/reverse')) {
      return REVERSAL_API_URL;
    }
    return BALANCE_API_URL;
  }
  if (p.startsWith('/beneficiaries')) {
    return BENEFICIARY_API_URL;
  }
  if (p.startsWith('/transfers')) {
    return TRANSFER_API_URL;
  }
  if (p.startsWith('/notifications')) {
    return NOTIFICATION_API_URL;
  }
  // Default: balance service (no unmapped paths are used today).
  return BALANCE_API_URL;
}

const JSON_CONTENT_TYPE = 'application/json';

/** Options for a single request. */
export interface RequestOptions {
  /**
   * Optional idempotency key sent as the `Idempotency-Key` header so retried
   * transfers/reversals are safe (design: Transfer_Service / Reversal_Service).
   */
  readonly idempotencyKey?: string;
  /** Optional query-string parameters; `undefined` values are omitted. */
  readonly query?: Readonly<Record<string, string | undefined>>;
}

/** Build a query string from a params object, skipping `undefined` values. */
function buildQuery(query: RequestOptions['query']): string {
  if (query === undefined) {
    return '';
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) {
      params.set(key, value);
    }
  }
  const serialized = params.toString();
  return serialized.length > 0 ? `?${serialized}` : '';
}

/**
 * Parse a response body as JSON, tolerating an empty or non-JSON body by
 * returning `undefined` rather than throwing — so a bodyless 200/204 (e.g. a
 * DELETE) or a malformed error page still yields a usable value.
 */
async function parseJsonSafe(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) {
    return undefined;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/**
 * Convert a non-2xx response's parsed body into an {@link ApiError}. Uses the
 * standard error envelope when present; otherwise synthesises a generic body
 * from the HTTP status so callers always receive a typed error with a stable
 * code (`AUTH_REQUIRED` on 401, `SERVICE_UNAVAILABLE` on 5xx, else generic).
 */
function toApiError(status: number, parsed: unknown): ApiError {
  if (isApiErrorEnvelope(parsed)) {
    return new ApiError(status, parsed.error);
  }
  let code: ApiErrorBody['code'] = 'INTERNAL_ERROR';
  if (status === 401) {
    code = 'AUTH_REQUIRED';
  } else if (status === 403) {
    code = 'NOT_AUTHORIZED';
  } else if (status >= 500) {
    code = 'SERVICE_UNAVAILABLE';
  }
  const fallback: ApiErrorBody = {
    code,
    message: 'The request could not be completed. Please try again.',
  };
  return new ApiError(status, fallback);
}

/**
 * Perform an authenticated JSON request against a service path and decode the
 * success body as `T`. Any non-2xx response, or a transport-level failure,
 * throws an {@link ApiError} carrying the standard-envelope fields.
 *
 * @typeParam T - the expected success-body shape.
 */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: JSON_CONTENT_TYPE,
  };
  if (body !== undefined) {
    headers['Content-Type'] = JSON_CONTENT_TYPE;
  }
  // The bearer credential is the Cognito idToken (Slice C3) — the JWT the API
  // Gateway authorizer validates. Read only from the in-memory store.
  const accessToken = getAccessToken();
  if (accessToken !== undefined) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (options?.idempotencyKey !== undefined) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }

  const url = `${baseUrlForPath(path)}${path}${buildQuery(options?.query)}`;

  let response: Response;
  try {
    // No `credentials: 'include'` — Slice C3 drops the HttpOnly-cookie model
    // (the browser sends the idToken as a Bearer header), and omitting cookies
    // keeps cross-origin API Gateway CORS preflight simple.
    response = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // Network / DNS / TLS failure — never reached the server (status 0).
    throw new ApiError(0, {
      code: 'SERVICE_UNAVAILABLE',
      message: 'Unable to reach the service. Check your connection and try again.',
    });
  }

  const parsed = await parseJsonSafe(response);
  if (!response.ok) {
    throw toApiError(response.status, parsed);
  }
  return parsed as T;
}

/** `GET` a JSON resource. */
export function apiGet<T>(path: string, options?: RequestOptions): Promise<T> {
  return request<T>('GET', path, undefined, options);
}

/** `POST` a JSON body and decode the JSON response. */
export function apiPost<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  return request<T>('POST', path, body ?? {}, options);
}

/** `PUT` a JSON body and decode the JSON response. */
export function apiPut<T>(
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  return request<T>('PUT', path, body ?? {}, options);
}

/** `DELETE` a resource; resolves once the deletion is acknowledged. */
export function apiDelete<T = void>(
  path: string,
  options?: RequestOptions,
): Promise<T> {
  return request<T>('DELETE', path, undefined, options);
}

/**
 * Generate a random idempotency key for a mutating request. Prefers the
 * platform `crypto.randomUUID` and falls back to a timestamp+random string in
 * environments where it is unavailable.
 */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto;
  if (c !== undefined && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
